<template>
  <div 
    class="premium-avatar" 
    :style="avatarStyle"
    :class="[shapeClass, { 'has-image': !!image }]"
  >
    <img 
      v-if="image" 
      :src="image" 
      :alt="name" 
      @error="imageError = true"
      v-show="!imageError"
      class="avatar-image"
    />
    <div v-if="!image || imageError" class="avatar-fallback" :style="fallbackStyle">
      {{ initials }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'PremiumAvatar',
  props: {
    image: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    size: {
      type: [String, Number],
      default: 40
    },
    variant: {
      type: String,
      default: 'circle', // 'circle' or 'squircle'
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    background: {
      type: String,
      default: null // Can be a custom gradient or color
    }
  },
  data() {
    return {
      imageError: false
    };
  },
  watch: {
    image() {
      this.imageError = false;
    }
  },
  computed: {
    initials() {
      if (!this.name) return '?';
      const words = this.name.trim().split(/\s+/);
      if (words.length === 0) return '?';
      
      // Logic:
      // 1 word -> 1st letter
      // 2 words -> 1st letter of each
      // 3+ words -> 1st letter of first 2 words
      return words.slice(0, 2)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
    },
    avatarStyle() {
      const s = typeof this.size === 'number' ? `${this.size}px` : this.size;
      return {
        width: s,
        height: s,
        minWidth: s,
        minHeight: s
      };
    },
    fallbackStyle() {
      const styles = {};
      
      if (this.fontSize) {
        styles.fontSize = typeof this.fontSize === 'number' ? `${this.fontSize}px` : this.fontSize;
      } else {
        // Auto font size based on avatar size
        const s = parseInt(this.size);
        if (!isNaN(s)) {
          styles.fontSize = `${Math.max(10, Math.floor(s * 0.4))}px`;
        }
      }

      if (this.background) {
        styles.background = this.background;
      } else {
        // Default premium gradient based on name hash or fixed
        styles.background = this.getGradientByName(this.name);
      }

      return styles;
    },
    shapeClass() {
      return this.variant === 'squircle' ? 'is-squircle' : 'is-circle';
    }
  },
  methods: {
    getGradientByName() {
      // Always use the primary brand gradient (Orange)
      return 'linear-gradient(135deg, #F26522, #ef4444)';
    }
  }
};
</script>

<style scoped>
.premium-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  user-select: none;
  background: var(--bg-primary, #f1f5f9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.premium-avatar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.is-circle {
  border-radius: 50%;
}

.is-squircle {
  border-radius: 28%; /* Squircle feel */
}

/* Specific for Society request if needed */
.is-squircle.premium-avatar {
  border-radius: 16px; 
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
  font-family: 'Inter', sans-serif;
}

.has-image {
  border: 2px solid var(--card-bg, #fff);
}
</style>
